import { DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Event, NavigationEnd, NavigationStart, Router } from '@angular/router';
import {
  BreadcrumbService,
  FragmentScrollService,
  LoaderService,
  MessagesService,
  OciService,
  OverflowHiddenService,
  ScrollService,
  SeoService,
} from '@app/services';
import {
  AppUtilsService,
  DataLayerService,
  DlRecosService,
  ExactagService,
} from 'haba-base-components';
import { Debounce } from 'haba-contentful-components';
import { IS_BROWSER, StatePersistenceService, StorageSyncType, WindowRef } from 'haba-core';
import { BREAKPOINT, BreakpointService } from 'haba-storefrontlib';
import { of } from 'rxjs';
import { distinctUntilChanged, filter, take, tap } from 'rxjs/operators';
import { LocalStorageKeys } from 'src/app/@enums/storage-keys';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  isCheckout: boolean;
  isOrderConfirmation: boolean;

  urlCheckoutPatterns = ['/checkout/', '/checkout-login'];
  urlOrderConfirmationPattern = '/order-confirmation';

  dataLayerNoScriptSafeUrl: SafeResourceUrl;
  inScroll: boolean = false;

  private isDesktop: boolean = false;
  private subscriptions = [];
  private currentUrl: string = '';
  private lastNavigationTrigger: NavigationStart['navigationTrigger'] = 'imperative';

  constructor(
    private seoService: SeoService, // Do not remove! Ensures hreflangs are initialized on every page including homepage
    private appUtilsService: AppUtilsService,
    private cref: ChangeDetectorRef,
    private dataLayerService: DataLayerService,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private router: Router,
    private sanitizer: DomSanitizer,
    private statePersistenceService: StatePersistenceService,
    protected winRef: WindowRef,
    public breadcrumbService: BreadcrumbService,
    public loaderService: LoaderService,
    public overflowHiddenService: OverflowHiddenService,
    public scrollService: ScrollService,
    private fragmentScrollService: FragmentScrollService,
    private breakpointService: BreakpointService,
    private exactagService: ExactagService,
    @Inject(DOCUMENT) private document: Document,
    @Inject(IS_BROWSER) private isBrowser: boolean,
    private ociService: OciService,
    private dlRecosService: DlRecosService,
    private messagesService: MessagesService
  ) {}

  ngOnInit() {
    this.messagesService.showNewsletterUnsubscribeMessage();

    this.subscriptions.push(
      this.router.events
        .pipe(
          tap((event) => this.handleScrollRestoration(event)),
          filter((event) => event instanceof NavigationEnd)
        )
        .subscribe(() => {
          try {
            this.appUtilsService.memoizeUrls();
            this.isCheckout = !!this.urlCheckoutPatterns.find((pattern) =>
              this.router.url.includes(pattern)
            );
            this.isOrderConfirmation = this.router.url.includes(this.urlOrderConfirmationPattern);

            this.breadcrumbService.clearItems(this.router.url);

            this.triggerExactag();

            setTimeout(() => this.fragmentScrollService.scrollToFragment());
            setTimeout(() => this.dataLayerService.setDataLayer(this.router.url));
            setTimeout(() => this.dlRecosService.resetVisitedRecos());

            this.handleMoinAiButton(this.router.url);

            this.overflowHiddenService.removeOverflow();
            this.cref.detectChanges();
          } catch (e) {
            console.error(e);
          }
        })
    );
    this.dataLayerNoScriptSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.dataLayerService.getNoScriptUrl()
    );

    this.subscriptions.push(
      this.scrollService.inScroll$.subscribe((bool) => {
        bool
          ? this.renderer.addClass(document.body, 'inScroll')
          : this.renderer.removeClass(document.body, 'inScroll');
      })
    );

    this.subscriptions.push(
      this.breakpointService
        .isUp(BREAKPOINT.md)
        .pipe(distinctUntilChanged())
        .subscribe((isDesktop) => {
          this.isDesktop = isDesktop;
          this.cref.detectChanges();
        })
    );
  }

  ngAfterViewInit(): void {
    this.saveScreenMetadata();

    if (this.isBrowser && this.ociService.isOciSession) {
      this.document.body.classList.add('oci-session');
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  // Is used by the ImageComponent from haba-libraries to read information about widths and heights and make proper calculations based on it
  @HostListener('window:resize', ['$event'])
  @Debounce(1000)
  onResize() {
    this.saveScreenMetadata();
  }

  // Is used by the ImageComponent from haba-libraries to read information about widths and heights and make proper calculations based on it
  private saveScreenMetadata() {
    const containerElement = this.elementRef.nativeElement.querySelector('.container');
    const screenElement = this.elementRef.nativeElement.querySelector('cx-storefront');
    if (containerElement && screenElement) {
      const metadata = {
        width: {
          container: containerElement.offsetWidth,
          screen: screenElement.offsetWidth,
        },
      };

      this.statePersistenceService.syncWithStorage({
        key: LocalStorageKeys.SCREEN,
        state$: of(metadata),
        storageType: StorageSyncType.LOCAL_STORAGE,
      });
    }
  }

  private triggerExactag(): void {
    this.exactagService.exactagInitiated$.pipe(filter(Boolean), take(1)).subscribe(() => {
      this.exactagService.runExactag(environment.exactag, this.router.url);
    });
  }

  handleMoinAiButton(url: string): void {
    if (!this.winRef.isBrowser() || this.isDesktop) return;

    const moinAiButton = this.winRef.document.getElementById('moin-ai-button');
    const moinAiTeaser = this.winRef.document.getElementById('moin-ai-teaser');

    if (url.includes('/cart') || url.includes('/p/')) {
      if (moinAiButton) moinAiButton.style.transform = 'translateY(-3.875rem)';
      if (moinAiTeaser) moinAiTeaser.style.transform = 'translateY(-3.875rem)';
    } else {
      if (moinAiButton) moinAiButton.style.transform = 'translateY(0)';
      if (moinAiTeaser) moinAiTeaser.style.transform = 'translateY(0)';
    }
  }

  private handleScrollRestoration(event: Event): void {
    if (!this.winRef.isBrowser()) {
      return;
    }

    if (this.scrollService.skipScroll) {
      this.scrollService.skipScroll = false;
      return;
    }

    if (event instanceof NavigationStart) {
      this.lastNavigationTrigger = event.navigationTrigger;
      // Save the current scroll position
      this.scrollService.savePosition(this.currentUrl);
    } else if (event instanceof NavigationEnd) {
      // Update the current URL
      this.currentUrl = event.urlAfterRedirects;

      if (this.router.parseUrl(this.currentUrl).fragment) return;

      // Only restore a saved scroll position on genuine browser back/forward
      // (popstate) navigation. For imperative navigations (e.g. clicking a link
      // such as the "Zurück zu ..." back link) always scroll to top.
      const savedPosition =
        this.lastNavigationTrigger === 'popstate'
          ? this.scrollService.getPosition(this.currentUrl)
          : null;
      if (savedPosition) {
        setTimeout(() => {
          window.scrollTo(savedPosition[0], savedPosition[1]);
        }, 1000);
      } else {
        window.scrollTo(0, 0); // Default scroll to top
      }
    }
  }
}
